import type { SortItem, SortEvent, SortAlgorithm } from '@/core/types';
import { EventRecorderImpl } from '@/core/EventRecorderImpl';

export class HeapSort implements SortAlgorithm {
  readonly name = 'Heap Sort';
  readonly category = 'comparison' as const;
  readonly stable = false;
  readonly inPlace = true;
  readonly adaptive = false;
  readonly best = 'O(n log n) - all cases same';
  readonly average = 'O(n log n) - typical';
  readonly worst = 'O(n log n) - guaranteed';
  readonly space = 'O(1) - in-place heap';

  run(input: SortItem[]): SortEvent[] {
    const recorder = new EventRecorderImpl();
    const arr = input.map(item => ({ ...item }));
    const n = arr.length;

    const heapify = (size: number, root: number) => {
      let largest = root;
      const left = 2 * root + 1;
      const right = 2 * root + 2;

      recorder.record({ type: 'heapify', root, size });

      if (left < size) {
        recorder.record({ type: 'compare', indices: [left, largest] });
        if (arr[left].value > arr[largest].value) largest = left;
      }
      if (right < size) {
        recorder.record({ type: 'compare', indices: [right, largest] });
        if (arr[right].value > arr[largest].value) largest = right;
      }
      if (largest !== root) {
        recorder.record({ type: 'swap', indices: [root, largest] });
        const t = arr[root]; arr[root] = arr[largest]; arr[largest] = t;
        arr[root].currentIndex = root;
        arr[largest].currentIndex = largest;
        heapify(size, largest);
      }
    };

    // build max-heap
    for (let i = Math.floor(n / 2) - 1; i >= 0; i--) heapify(n, i);
    // extract
    for (let i = n - 1; i > 0; i--) {
      recorder.record({ type: 'swap', indices: [0, i] });
      const t = arr[0]; arr[0] = arr[i]; arr[i] = t;
      arr[0].currentIndex = 0;
      arr[i].currentIndex = i;
      heapify(i, 0);
    }

    recorder.record({ type: 'done', indices: arr.map((_, i) => i) });
    return recorder.events;
  }
}
